import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * UI-local state — not persisted to workflow JSON, not synced across coop
 * sessions. Lives here to escape Index.tsx and stop being prop-drilled to
 * shells, sidebars, and CanvasArea.
 *
 * - selectedGroupId:     which group's properties panel is showing
 * - selectedConnectorId: which connector is selected (highlighted, shown
 *                        in PropertiesPanel, deletable via Delete-key).
 *                        Mutually exclusive with block/group selection.
 * - connectMode:    is the user wiring up connectors (mutex with groupMode)
 * - groupMode:      is the user picking blocks to bundle into a group
 * - groupSelection: which blocks are picked while groupMode is active
 *
 * crossSheetMode is intentionally NOT here — it lives in useCrossSheetLinking
 * which owns its own source-block ref + Esc handler.
 */
export interface SelectionModeContextValue {
  selectedGroupId: string | null;
  setSelectedGroupId: (id: string | null) => void;
  selectedConnectorId: string | null;
  setSelectedConnectorId: (id: string | null) => void;
  connectMode: boolean;
  setConnectMode: (on: boolean) => void;
  groupMode: boolean;
  setGroupMode: (on: boolean) => void;
  groupSelection: string[];
  setGroupSelection: (ids: string[]) => void;
}

const SelectionModeContext = createContext<SelectionModeContextValue | null>(null);

export function SelectionModeProvider({ children }: { children: ReactNode }) {
  const [selectedGroupId, setSelectedGroupIdRaw] = useState<string | null>(null);
  const [selectedConnectorId, setSelectedConnectorIdRaw] = useState<string | null>(null);
  const [connectMode, setConnectMode] = useState(false);
  const [groupMode, setGroupMode] = useState(false);
  const [groupSelection, setGroupSelection] = useState<string[]>([]);

  // Mutex: selecting a group clears connector selection (and vice versa).
  // Block selection lives in useMultiSheetWorkflow; the call-sites that
  // setSelectedBlockId are also responsible for clearing these two.
  const setSelectedGroupId = useCallback((id: string | null) => {
    setSelectedGroupIdRaw(id);
    if (id !== null) setSelectedConnectorIdRaw(null);
  }, []);
  const setSelectedConnectorId = useCallback((id: string | null) => {
    setSelectedConnectorIdRaw(id);
    if (id !== null) setSelectedGroupIdRaw(null);
  }, []);

  const value = useMemo<SelectionModeContextValue>(() => ({
    selectedGroupId, setSelectedGroupId,
    selectedConnectorId, setSelectedConnectorId,
    connectMode, setConnectMode,
    groupMode, setGroupMode,
    groupSelection, setGroupSelection,
  }), [selectedGroupId, setSelectedGroupId, selectedConnectorId, setSelectedConnectorId, connectMode, groupMode, groupSelection]);

  return <SelectionModeContext.Provider value={value}>{children}</SelectionModeContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSelectionMode(): SelectionModeContextValue {
  const ctx = useContext(SelectionModeContext);
  if (!ctx) {
    throw new Error('useSelectionMode must be used within a <SelectionModeProvider>');
  }
  return ctx;
}
